import React, { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { clientService } from "../Client.service";
import { Client } from "@/types";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { PlusIcon } from "@/components/icons";

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: (client: Client) => void;
}

export const CreateClientModal: React.FC<CreateClientModalProps> = ({
  isOpen,
  onClose,
  onClientCreated,
}) => {
  const [clientName, setClientName] = useState("");
  const [expectedFacebookPageName, setExpectedFacebookPageName] = useState("");
  const [expectedInstagramUsername, setExpectedInstagramUsername] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientName.trim()) {
      setError("Client name is required");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const result = await clientService.createClient(
        clientName.trim(),
        expectedFacebookPageName.trim() || undefined,
        expectedInstagramUsername.trim() || undefined
      );

      if (result.success && result.client) {
        console.log("✅ Client created successfully:", result.client);
        onClientCreated(result.client);
        setClientName("");
        onClose();
      } else {
        setError(result.error || "Failed to create client");
      }
    } catch (err) {
      console.error("❌ Unexpected error creating client:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setClientName("");
      setExpectedFacebookPageName("");
      setExpectedInstagramUsername("");
      setError(null);
      onClose();
    }
  };

  const footerContent = (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={handleClose}
        disabled={isCreating}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="create-client-form"
        disabled={isCreating || !clientName.trim()}
        className="flex items-center gap-2"
      >
        {isCreating ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Creating...
          </>
        ) : (
          <>
            <PlusIcon className="w-4 h-4" />
            {ENTITY_CONFIG.client.createLabel}
          </>
        )}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={ENTITY_CONFIG.client.createLabel}
      size="md"
      footer={footerContent}
    >
      <form
        id="create-client-form"
        onSubmit={handleSubmit}
        className="p-6 space-y-6"
      >
        <div>
          <label
            htmlFor="clientName"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Client Name
          </label>
          <input
            id="clientName"
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Enter client name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
            disabled={isCreating}
            autoFocus
          />
          <p className="text-xs text-gray-500 mt-1">
            This will create a new client with full workspace setup (Google
            Drive, etc.)
          </p>
        </div>

        <div>
          <label
            htmlFor="expectedFacebookPageName"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Expected Facebook Page Name (Optional)
          </label>
          <input
            id="expectedFacebookPageName"
            type="text"
            value={expectedFacebookPageName}
            onChange={(e) => setExpectedFacebookPageName(e.target.value)}
            placeholder="e.g., My Business Page"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
            disabled={isCreating}
          />
          <p className="text-xs text-gray-500 mt-1">
            The exact name of the Facebook page they should connect
          </p>
        </div>

        <div>
          <label
            htmlFor="expectedInstagramUsername"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Expected Instagram Username (Optional)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-500 text-sm">@</span>
            <input
              id="expectedInstagramUsername"
              type="text"
              value={expectedInstagramUsername}
              onChange={(e) => setExpectedInstagramUsername(e.target.value)}
              placeholder="username"
              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
              disabled={isCreating}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            The exact Instagram username they should connect (without @)
          </p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg
                className="w-4 h-4 text-red-500 mt-0.5 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-red-700 text-sm">{error}</div>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

